// File: frontend/src/pages/FitenkoToday.js
// Version: 1.2 (2026-10-16, Wave 1 Translation & Shell Unification)
// Description: экран «Сегодня» арендатора fitenko и eln вместо общего /dashboard —
//             ЧЕТЫРЕ блока на СУЩЕСТВУЮЩИХ ручках + карточка срока, а для ELN
//             дополнительно блоки метрик внизу.
//
// 🔴 УЗОРА ЗАМЫСЛА В ЭТОМ ФАЙЛЕ БОЛЬШЕ НЕТ. Шапка страницы и оболочка блока
//    переехали в components/FitenkoUzor.js (разметка) и в раздел 7.6
//    styles/fitenko-shell.css (оформление) — по одному держателю на каждое.
//    Здесь остались только СВОИ строчные примитивы: список, строка, время,
//    заголовок, таблетка, кнопка. Потребитель у них один — этот экран, и
//    выносить форму по одному потребителю значило бы её угадывать.
//
// 🔴 РУБЕЖ АРЕНДАТОРА СТОИТ В Dashboard.js: этот файл рисуется
//    из-под `isRail` (isFitenko || features?.shell === 'rail').
//
// 🔴 НИ ОДНОЙ ВЕЛИЧИНЫ, КОТОРОЙ НЕТ В ОТВЕТЕ РУЧКИ. Чего на экране НЕТ и почему:
//    (а) ПЛИТОК-СЧЁТЧИКОВ (ACTIVE BATTLES / BATTLES WON) нет вовсе. Они считали
//        работу СИСТЕМЫ, а не решения человека; у MLG они остались на месте.
//    (б) ТЁМНАЯ КАРТОЧКА «ГЛАВНОЕ СЕЙЧАС» ПОЯВИЛАСЬ (quick-260910-jmn) — и только
//        потому, что появилась ось отбора: СРОК. Он либо наступил, либо нет,
//        оценивать нечего. Токен --council-bg применён РОВНО ЗДЕСЬ и больше
//        нигде. Внутри карточки нет ни процента, ни уровня, ни шкалы: за ними
//        не стоит вычисления, а единственная крупная величина — число
//        оставшихся дней — посчитана НА СЕРВЕРЕ как разность двух дат.
//        🔴 ПУСТАЯ КАРТОЧКА — ОСНОВНОЙ СЛУЧАЙ, А НЕ ЗАГЛУШКА: на бою дел два и
//        сроков у них нет, так что в день выкатки её видят пустой все. Она
//        говорит, ЧЕГО нет, называет число и ведёт туда, где срок заводится.
//        «Ближайшего срока нет» и «срочного нет» — разные утверждения, и
//        последняя строка карточки запрещает их спутать.
//    (в) ПИЛЮЛИ «всё синхронизировано» нет: ручки состояния синхронизаций нет,
//        и зелёная пилюля была бы утверждением о том, чего не мерили.
//    (г) КНОПКИ «Request translation» нет: у ручки почты ровно два глагола,
//        approve и dismiss, перевода среди них нет.
//    (д) СВОЕГО ЧИСЛА ВСТРЕЧ блок встреч не печатает: counters.calendar считает
//        НЕДЕЛЮ, и рядом с дневным списком это были бы две разные величины под
//        одной подписью.
//    (е) СВОЕГО ИТОГА блок задач не печатает: правило «не DONE» повторено здесь
//        на клиенте, а на сервере оно живёт в nav_counters. Согласовать двух
//        держателей нечем, поэтому число остаётся ТОЛЬКО в колонке.
//
// 🔴 ТРИ СОСТОЯНИЯ У КАЖДОГО ЧИСЛА: число / ноль / НЕ ЗНАЕМ. «Не знаем» нигде не
//    нарисовано нулём — форму числа даёт ЕДИНСТВЕННЫЙ держатель chisloDlyaPunkta.
//    [[feedback_unknown_is_not_no]]
//
// 🔴 ОТКАЗ И СБОЙ ГОВОРЯТ РАЗНЫМИ СЛОВАМИ: 403 значит «не положено», прочее —
//    «не смогли спросить». Пустой список не означает ни того, ни другого.
//
// 🔴 ЧЕТЫРЕ КЛАССА БЕЗ ОФОРМЛЕНИЯ — НАМЕРЕННО, А НЕ ЗАБЫТЫ. fk-day-meets,
//    fk-day-inbox, fk-day-feed, fk-day-tasks не имеют ни одного правила в
//    разделе 9: они не красят, а ИМЕНУЮТ блок — тем же приёмом, каким это
//    делает fk-hist-card--<тип> на экране «История».

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import apiService from '../services/apiService';
import taskService from '../services/taskService';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useUiLanguage } from '../context/UiLanguageContext';
import { useNavCounters } from '../hooks/useNavCounters';
import { chisloDlyaPunkta } from '../components/FitenkoRail';
import { ShapkaEkrana, ObolochkaBloka, Sostoyanie } from '../components/FitenkoUzor';
import { metkaTipa, znachokTipa } from '../config/historyEvents';
import ElnMetricCards from '../components/ElnMetricCards';
import { formatEventTitle, formatLeadName } from '../utils/historyFormatters';

// Держатели пределов показа. Каждое число названо и живёт в одном месте.
const PREDEL_SVODKI = 160;   // где обрезается ai_summary в свёрнутой строке
const POKAZ_LENTY = 8;       // сколько событий ленты показывает экран

const PUSTO = { rod: 'gruzim', dannye: [], prichina: null };

// Урезание. Многоточие НАЗЫВАЕТ обрезку: текст, оборванный без знака, читается
// как весь текст.
const urezat = (t, predel) => (String(t || '').length > predel
    ? `${String(t).slice(0, predel)}…`
    : String(t || ''));

const privetstvie = (t) => {
    const h = new Date().getHours();
    const kluch = h < 12 ? 'firm_today.greeting.morning' : h < 17 ? 'firm_today.greeting.afternoon' : 'firm_today.greeting.evening';
    return t(kluch);
};

const vremya = (iso, lang) => {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime())) return '—';   // ПРОЧЕРК, А НЕ ВЫДУМАННОЕ ВРЕМЯ
    return d.toLocaleTimeString(lang === 'ru' ? 'ru-RU' : 'en-US', { hour: '2-digit', minute: '2-digit' });
};

const data = (iso, lang) => {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', { day: 'numeric', month: 'long' });
};

// Дата срока разбирается ПО ЧАСТЯМ, а не через new Date(строка). Голая дата
// «ГГГГ-ММ-ДД» читается как полночь UTC, и западнее Гринвича она съезжала бы на
// сутки назад — то есть срок печатался бы не тем днём, каким его поставили.
const dataSroka = (iso, lang) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return null;   // ПРОЧЕРК, А НЕ ВЫДУМАННАЯ ДАТА
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
        .toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });
};

const metkaSobytiya = (tip, t, lang) => {
    if (lang === 'ru') return metkaTipa(tip);
    const k = `firm_today.event_type.${tip}`;
    const tr = t(k);
    return tr === `[${k}]` ? metkaTipa(tip) : tr;
};

// Форма числа берётся у ЕДИНСТВЕННОГО держателя. «Не назвали» рисуется прочерком,
// а не нулём: ноль — измеренная величина, прочерк — её отсутствие.
const schet = (v) => chisloDlyaPunkta({ value: v }) || '—';

// Границы суток в местном времени браузера. Обе — ISO, как требует ручка.
const sutki = () => {
    const n = new Date();
    const s = new Date(n.getFullYear(), n.getMonth(), n.getDate());
    const e = new Date(s.getTime() + 24 * 60 * 60 * 1000);
    return [s.toISOString(), e.toISOString()];
};

const rod = (e) => (e && e.status === 403 ? 'otkaz' : 'sboj');

const FitenkoToday = () => {
    const { user } = useAuth();
    const { isFitenko, features } = useBranding();
    const { t, lang } = useUiLanguage();
    const counters = useNavCounters();

    const [vstrechi, postavitVstrechi] = useState(PUSTO);
    const [pisma, postavitPisma] = useState(PUSTO);
    const [lenta, postavitLentu] = useState(PUSTO);
    const [zadachi, postavitZadachi] = useState(PUSTO);
    const [raskryto, postavitRaskryto] = useState(null);   // id письма с раскрытым черновиком
    const [zanyat, postavitZanyat] = useState(null);       // id письма, по которому идёт действие
    const [tabletka, postavitTabletku] = useState(null);
    const [srok, postavitSrok] = useState(PUSTO);

    useEffect(() => {
        let zhiv = true;
        const [s, e] = sutki();
        apiService.getCalendarEvents(s, e)
            .then((r) => { if (zhiv) postavitVstrechi({ rod: 'gotovo', dannye: Array.isArray(r) ? r : [], prichina: null }); })
            .catch((err) => { if (zhiv) postavitVstrechi({ rod: rod(err), dannye: [], prichina: (err && err.message) || t('firm_today.reason_not_specified') }); });
        return () => { zhiv = false; };
    }, [t]);

    const mailNotConnected = counters.inbox && counters.inbox.reason === 'mail_not_connected';

    // Список писем перечитывается ЦЕЛИКОМ после каждого действия. Клиентский
    // вычерк строки разошёлся бы с сервером на первом же отказе отправки.
    const zagruzitPisma = useCallback(() => {
        postavitPisma((p) => ({ ...p, rod: 'gruzim' }));
        return apiService.getPendingEmails()
            .then((r) => postavitPisma({ rod: 'gotovo', dannye: Array.isArray(r) ? r : [], prichina: null }))
            .catch((err) => postavitPisma({ rod: rod(err), dannye: [], prichina: (err && err.message) || t('firm_today.reason_not_specified') }));
    }, [t]);

    useEffect(() => {
        if (!mailNotConnected) {
            zagruzitPisma();
        }
    }, [zagruzitPisma, mailNotConnected]);

    // Срок. Приём тот же, что у четырёх соседних блоков: отказ и сбой говорят
    // РАЗНЫМИ словами, пустой ответ не означает ни того, ни другого.
    useEffect(() => {
        let zhiv = true;
        apiService.getBlizhayshiySrok()
            .then((r) => { if (zhiv) postavitSrok({ rod: 'gotovo', dannye: r || {}, prichina: null }); })
            .catch((err) => { if (zhiv) postavitSrok({ rod: rod(err), dannye: {}, prichina: (err && err.message) || t('firm_today.reason_not_specified') }); });
        return () => { zhiv = false; };
    }, [t]);

    useEffect(() => {
        let zhiv = true;
        apiService.getFirmHistory()
            .then((r) => { if (zhiv) postavitLentu({ rod: 'gotovo', dannye: r || {}, prichina: null }); })
            .catch((err) => { if (zhiv) postavitLentu({ rod: rod(err), dannye: {}, prichina: (err && err.message) || t('firm_today.reason_not_specified') }); });
        return () => { zhiv = false; };
    }, [t]);

    useEffect(() => {
        let zhiv = true;
        taskService.getTasks({ assigned_to_me: true })
            .then((r) => { if (zhiv) postavitZadachi({ rod: 'gotovo', dannye: Array.isArray(r) ? r : [], prichina: null }); })
            .catch((err) => { if (zhiv) postavitZadachi({ rod: rod(err), dannye: [], prichina: (err && err.message) || t('firm_today.reason_not_specified') }); });
        return () => { zhiv = false; };
    }, [t]);

    const otkrytye = useMemo(
        () => (zadachi.dannye || []).filter((z) => z && z.status !== 'DONE'),
        [zadachi.dannye],
    );

    // Таблетка рисуется ТОЛЬКО если за ней есть хоть одна задача В ЗАГРУЖЕННОМ
    // ОТВЕТЕ — приём с экрана «История».
    const tabletki = useMemo(() => [
        { kluch: 'ai', metka: t('firm_today.tasks.pill_ai'), otbor: (z) => z.is_ai_generated === true },
        { kluch: 'hot', metka: t('firm_today.tasks.pill_hot'), otbor: (z) => ['HIGH', 'URGENT'].includes(z.priority) },
    ].map((tab) => ({ ...tab, n: otkrytye.filter(tab.otbor).length })).filter((tab) => tab.n > 0), [otkrytye, t]);

    const vidimyeZadachi = useMemo(() => {
        const tab = tabletki.find((x) => x.kluch === tabletka);
        return tab ? otkrytye.filter(tab.otbor) : otkrytye;
    }, [otkrytye, tabletki, tabletka]);

    const sobytiya = useMemo(() => (lenta.dannye && lenta.dannye.events) || [], [lenta.dannye]);
    const imena = (lenta.dannye && lenta.dannye.lead_names) || null;

    const deystvie = (id, obeshanie) => {
        postavitZanyat(id);
        obeshanie.then(() => zagruzitPisma())
            .catch((err) => postavitPisma((p) => ({ ...p, rod: rod(err), prichina: (err && err.message) || t('firm_today.reason_not_specified') })))
            .finally(() => { postavitZanyat(null); postavitRaskryto(null); });
    };

    // Строка о письмах на решении. Три состояния, и «не узнали» — не ноль.
    const chisloPisem = chisloDlyaPunkta(counters.inbox);
    let proPisma;
    if (mailNotConnected) {
        proPisma = t('firm_today.inbox.not_connected');
    } else if (chisloPisem === null) {
        proPisma = t('firm_today.inbox.unknown');
    } else if (chisloPisem === '0') {
        proPisma = t('firm_today.inbox.none');
    } else {
        proPisma = t('firm_today.inbox.pending', { count: chisloPisem });
    }

    // Величины карточки берутся КАЖДАЯ СВОИМ полем ответа и не выводятся одна из
    // другой: «сколько названо поимённо» — длина присланного списка.
    const bezSroka = (srok.dannye && srok.dannye.bez_sroka) || {};
    const nazvany = bezSroka.pokazano || [];

    const kalendarNePodklyuchen = !!(counters.calendar && counters.calendar.reason === 'calendar_not_connected');

    return (
        <div className="fk-page">
            {/* Имя не подставляется, если его не прислали: «Partner» вместо
                имени было бы выдумкой на первой строке экрана. */}
            <ShapkaEkrana
                nadzagolovok={t('firm_today.header.pre')}
                zagolovok={`${privetstvie(t)}${user && user.first_name ? `, ${user.first_name}` : ''}`}
                podstroka={`${new Date().toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                })} · ${proPisma}`}
                kuda="/email-inbox"
                podpis={t('firm_today.header.open_inbox')} />

            {/* КАРТОЧКА «ГЛАВНОЕ СЕЙЧАС» — НАД сеткой из четырёх блоков, во всю ширину. */}
            <ObolochkaBloka klass="fk-day-srok" zagolovok={t('firm_today.priority.title')} kuda="/cases"
                  podpis={t('firm_today.priority.all_cases')}
                  ogovorka={t('firm_today.priority.caveat')}>
                {srok.rod === 'gruzim' ? <Sostoyanie tekst={t('firm_today.priority.loading')} /> : null}
                {srok.rod === 'otkaz'
                    ? <Sostoyanie tekst={t('firm_today.priority.denied', { reason: srok.prichina || t('firm_today.reason_not_specified') })} /> : null}
                {srok.rod === 'sboj'
                    ? <Sostoyanie tekst={t('firm_today.priority.failed', { reason: srok.prichina || t('firm_today.reason_not_specified') })} /> : null}
                {srok.rod === 'gotovo' && srok.dannye.srok ? (
                    <>
                        <p className="fk-day-srok-dney">
                            {t('firm_today.priority.days_left', { count: schet(srok.dannye.srok.dney_ostalos) })}{' '}
                            <span className="fk-day-srok-kogda">{t('firm_today.priority.until', { date: dataSroka(srok.dannye.srok.deadline, lang) || '—' })}</span>
                        </p>
                        <p className="fk-day-srok-delo">
                            {srok.dannye.srok.case_number} — {srok.dannye.srok.title}{' '}
                            <Link className="fk-day-srok-ssylka" to={`/cases/${srok.dannye.srok.case_id}`}>{t('firm_today.priority.open_case')}</Link>
                        </p>
                        {srok.dannye.srok.deadline_reason
                            ? <p className="fk-day-srok-pochemu">{t('firm_today.priority.reason', { reason: srok.dannye.srok.deadline_reason })}</p>
                            : null}
                    </>
                ) : null}
                {srok.rod === 'gotovo' && !srok.dannye.srok
                    ? <Sostoyanie tekst={t('firm_today.priority.none_upcoming', { visible: schet(srok.dannye.vsego_del_vidno) })} /> : null}
                {srok.rod === 'gotovo' ? (
                    <>
                        <p className="fk-day-srok-schet">
                            {t('firm_today.priority.stats', {
                                without: schet(bezSroka.vsego),
                                visible: schet(srok.dannye.vsego_del_vidno),
                                unknown: schet(srok.dannye.priznano_neizvestnymi),
                                past: schet(srok.dannye.proshedshie),
                            })}
                        </p>
                        <p className="fk-day-srok-pokaz">
                            {t('firm_today.priority.named_stats', {
                                shown: schet(nazvany.length),
                                total: schet(bezSroka.vsego),
                            })}
                        </p>
                        <ul className="fk-day-srok-spisok">
                            {nazvany.map((delo) => (
                                <li className="fk-day-srok-punkt" key={delo.case_id}>
                                    <span>{delo.case_number} — {delo.title}</span>
                                    <Link className="fk-day-srok-ssylka" to={`/cases/${delo.case_id}`}>{t('firm_today.priority.set_deadline')}</Link>
                                </li>
                            ))}
                        </ul>
                    </>
                ) : null}
                {srok.rod === 'gotovo' && !srok.dannye.srok ? (
                    <p className="fk-day-srok-pochemu">
                        {t('firm_today.priority.not_none_urgent')}
                    </p>
                ) : null}
            </ObolochkaBloka>

            <div className="fk-day-cols">
                <ObolochkaBloka klass="fk-day-meets" zagolovok={t('firm_today.meetings.title')} kuda="/calendar"
                      podpis={t('firm_today.meetings.all_calendar')}
                      ogovorka={t('firm_today.meetings.caveat')}>
                    {kalendarNePodklyuchen
                        ? <Sostoyanie tekst={t('firm_today.meetings.not_connected')} />
                        : null}
                    {!kalendarNePodklyuchen && vstrechi.rod === 'gruzim'
                        ? <Sostoyanie tekst={t('firm_today.meetings.loading')} /> : null}
                    {!kalendarNePodklyuchen && vstrechi.rod === 'otkaz'
                        ? <Sostoyanie tekst={t('firm_today.meetings.denied', { reason: vstrechi.prichina || t('firm_today.reason_not_specified') })} /> : null}
                    {!kalendarNePodklyuchen && vstrechi.rod === 'sboj'
                        ? <Sostoyanie tekst={t('firm_today.meetings.failed', { reason: vstrechi.prichina || t('firm_today.reason_not_specified') })} /> : null}
                    {!kalendarNePodklyuchen && vstrechi.rod === 'gotovo' && vstrechi.dannye.length === 0
                        ? <Sostoyanie tekst={t('firm_today.meetings.empty')} /> : null}
                    {!kalendarNePodklyuchen && vstrechi.rod === 'gotovo' && vstrechi.dannye.length > 0 ? (
                        <ul className="fk-day-list">
                            {vstrechi.dannye.map((v, i) => (
                                <li className="fk-day-row" key={v.id || `${v.summary}-${i}`}>
                                    <span className="fk-day-time">{vremya(v.start && v.start.dateTime, lang)}</span>
                                    <span className="fk-day-main">
                                        <span className="fk-day-title">{v.summary || '—'}</span>
                                        {v.attorney_name ? <span className="fk-day-who">{v.attorney_name}</span> : null}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </ObolochkaBloka>

                <ObolochkaBloka klass="fk-day-inbox" zagolovok={t('firm_today.inbox_block.title')} kuda="/email-inbox"
                      podpis={t('firm_today.inbox_block.all_inbox')}
                      ogovorka={t('firm_today.inbox_block.caveat')}>
                    {mailNotConnected ? <Sostoyanie tekst={t('firm_today.inbox_block.not_connected')} /> : null}
                    {!mailNotConnected && pisma.rod === 'gruzim' ? <Sostoyanie tekst={t('firm_today.inbox_block.loading')} /> : null}
                    {!mailNotConnected && pisma.rod === 'otkaz'
                        ? <Sostoyanie tekst={t('firm_today.inbox_block.denied', { reason: pisma.prichina || t('firm_today.reason_not_specified') })} /> : null}
                    {!mailNotConnected && pisma.rod === 'sboj'
                        ? <Sostoyanie tekst={t('firm_today.inbox_block.failed', { reason: pisma.prichina || t('firm_today.reason_not_specified') })} /> : null}
                    {!mailNotConnected && pisma.rod === 'gotovo' && pisma.dannye.length === 0
                        ? <Sostoyanie tekst={t('firm_today.inbox_block.empty')} /> : null}
                    {!mailNotConnected && pisma.rod === 'gotovo' && pisma.dannye.length > 0 ? (
                        <ul className="fk-day-list">
                            {pisma.dannye.map((p) => (
                                <li className="fk-day-row fk-day-row--stack" key={p.id}>
                                    <span className="fk-day-main">
                                        <span className="fk-day-title">{p.from_name || p.from_email || '—'}</span>
                                        {p.subject ? <span className="fk-day-meta">{p.subject}</span> : null}
                                        {p.ai_summary ? (
                                            <span className="fk-day-sum">{urezat(p.ai_summary, PREDEL_SVODKI)}</span>
                                        ) : null}
                                    </span>
                                    {p.ai_draft_reply ? (
                                        <button type="button" className="fk-day-chev"
                                                aria-label={raskryto === p.id ? t('firm_today.inbox_block.collapse_draft') : t('firm_today.inbox_block.expand_draft')}
                                                onClick={() => postavitRaskryto(raskryto === p.id ? null : p.id)}>
                                            <ChevronDown size={14} strokeWidth={1.5} aria-hidden="true" />
                                        </button>
                                    ) : null}
                                    {raskryto === p.id && p.ai_draft_reply ? (
                                        <span className="fk-day-open">
                                            <span className="fk-day-draft">{p.ai_draft_reply}</span>
                                            <span className="fk-day-acts">
                                                <button type="button" className="fk-day-btn fk-day-btn--ok"
                                                        disabled={zanyat === p.id}
                                                        onClick={() => deystvie(p.id, apiService.approveEmail(p.id))}>
                                                    {t('firm_today.inbox_block.approve_send')}
                                                </button>
                                                <button type="button" className="fk-day-btn"
                                                        disabled={zanyat === p.id}
                                                        onClick={() => deystvie(p.id, apiService.dismissEmail(p.id))}>
                                                    {t('firm_today.inbox_block.dismiss')}
                                                </button>
                                            </span>
                                        </span>
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </ObolochkaBloka>

                <ObolochkaBloka klass="fk-day-feed" zagolovok={t('firm_today.history.title')} kuda="/history"
                      podpis={t('firm_today.history.all_history')}
                      ogovorka={lenta.rod === 'gotovo'
                          ? t('firm_today.history.caveat', {
                              shown: Math.min(POKAZ_LENTY, sobytiya.length),
                              total: sobytiya.length,
                              truncated: (lenta.dannye && lenta.dannye.usecheno) ? t('firm_today.history.truncated') : '',
                          })
                          : null}>
                    {lenta.rod === 'gruzim' ? <Sostoyanie tekst={t('firm_today.history.loading')} /> : null}
                    {lenta.rod === 'otkaz'
                        ? <Sostoyanie tekst={t('firm_today.history.denied', { reason: lenta.prichina || t('firm_today.reason_not_specified') })} /> : null}
                    {lenta.rod === 'sboj'
                        ? <Sostoyanie tekst={t('firm_today.history.failed', { reason: lenta.prichina || t('firm_today.reason_not_specified') })} /> : null}
                    {lenta.rod === 'gotovo' && sobytiya.length === 0
                        ? <Sostoyanie tekst={t('firm_today.history.empty')} /> : null}
                    {lenta.rod === 'gotovo' && sobytiya.length > 0 ? (
                        <ul className="fk-day-list">
                            {sobytiya.slice(0, POKAZ_LENTY).map((e) => {
                                const Znachok = znachokTipa(e.event_type);
                                const imya = imena ? imena[String(e.lead_id)] : null;
                                return (
                                    <li className="fk-day-row" key={e.id}>
                                        <span className="fk-day-time">{vremya(e.created_at, lang)}</span>
                                        {Znachok ? <Znachok className="fk-day-icon" size={15} strokeWidth={1.5} aria-hidden="true" /> : null}
                                        <span className="fk-day-main">
                                            <span className="fk-day-type">{metkaSobytiya(e.event_type, t, lang)}</span>
                                            <span className="fk-day-title">{formatEventTitle(e.title, e.event_type, t, lang)}</span>
                                            {imya ? <span className="fk-day-who">{formatLeadName(imya, t, lang)}</span> : null}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}
                </ObolochkaBloka>

                <ObolochkaBloka klass="fk-day-tasks" zagolovok={t('firm_today.tasks.title')} kuda="/tasks"
                      podpis={t('firm_today.tasks.all_tasks')}
                      ogovorka={t('firm_today.tasks.caveat')}>
                    {zadachi.rod === 'gruzim' ? <Sostoyanie tekst={t('firm_today.tasks.loading')} /> : null}
                    {zadachi.rod === 'otkaz'
                        ? <Sostoyanie tekst={t('firm_today.tasks.denied', { reason: zadachi.prichina || t('firm_today.reason_not_specified') })} /> : null}
                    {zadachi.rod === 'sboj'
                        ? <Sostoyanie tekst={t('firm_today.tasks.failed', { reason: zadachi.prichina || t('firm_today.reason_not_specified') })} /> : null}
                    {zadachi.rod === 'gotovo' && otkrytye.length === 0
                        ? <Sostoyanie tekst={t('firm_today.tasks.empty')} /> : null}
                    {zadachi.rod === 'gotovo' && otkrytye.length > 0 ? (
                        <>
                            {tabletki.length > 0 ? (
                                <div className="fk-day-pills">
                                    <button type="button"
                                            className={`fk-day-pill${tabletka === null ? ' active' : ''}`}
                                            onClick={() => postavitTabletku(null)}>
                                        {t('firm_today.tasks.pill_all', { count: otkrytye.length })}
                                    </button>
                                    {tabletki.map((tab) => (
                                        <button key={tab.kluch} type="button"
                                                className={`fk-day-pill${tabletka === tab.kluch ? ' active' : ''}`}
                                                onClick={() => postavitTabletku(tab.kluch)}>
                                            {tab.metka} ({tab.n})
                                        </button>
                                    ))}
                                </div>
                            ) : null}
                            <ul className="fk-day-list">
                                {vidimyeZadachi.map((z) => (
                                    <li className="fk-day-row" key={z.id}>
                                        <span className="fk-day-main">
                                            <span className="fk-day-title">{z.title}</span>
                                            {z.due_date ? <span className="fk-day-meta">{t('firm_today.tasks.due', { date: data(z.due_date, lang) || z.due_date })}</span> : null}
                                            {z.is_ai_generated ? <span className="fk-day-flag">{t('firm_today.tasks.ai_flag')}</span> : null}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    ) : null}
                </ObolochkaBloka>
            </div>
            {(!isFitenko && features?.today_dashboard) && <ElnMetricCards />}
        </div>
    );
};

export default FitenkoToday;
